import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Quote, ArrowRight, Sparkles, Clock } from 'lucide-react';
import QuickAnswer from '../components/QuickAnswer';

const Results = () => {
  return (
    <div className="bg-gradient-to-b from-[#e8f4fd] to-[#f0f8ff] min-h-screen py-20">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 text-center">
          Real Stories. Real Structure.
        </h1>
        <p className="text-xl text-gray-600 text-center mb-10">
          What happens when you replace motivation with a system.
        </p>

        <QuickAnswer label="What to expect">
          Levanta is built for long-term results, not overnight transformation.
          Members who stay consistent for 90+ days consistently report three
          things: clearer priorities, more reliable routines, and a slow but
          steady sense that life is finally building on itself. These stories
          are early — the system is young — but the pattern is already visible.
        </QuickAnswer>

        <Card className="bg-blue-50 border-2 border-blue-200 rounded-3xl p-8 shadow-sm mb-10">
          <div className="flex items-start gap-4">
            <Sparkles className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Share your Levanta story
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Have you spent 30 days or more with Levanta? Your story could
                help the next person decide to build instead of scroll.
              </p>
              <Link to="/contact">
                <Button
                  data-testid="results-share-story-btn"
                  variant="outline"
                  className="border-blue-500 text-blue-600 hover:bg-white"
                >
                  Send us your story
                </Button>
              </Link>
            </div>
          </div>
        </Card>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">Early stories from the Community</h2>
        <div className="space-y-6 mb-12">
          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <Quote className="w-8 h-8 text-blue-500 mb-3" />
            <p className="text-lg text-gray-800 leading-relaxed mb-4">
              &ldquo;After a decade of jumping between productivity apps, Levanta
              is the first thing I&apos;ve stayed on for more than six weeks.
              The secret isn&apos;t the content — it&apos;s knowing exactly what
              to do with it tomorrow.&rdquo;
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700">
                D
              </div>
              <div className="text-sm">
                <p className="font-semibold text-gray-900">Daniel, 34</p>
                <p className="text-gray-500">Creation World, 90 days in</p>
              </div>
            </div>
          </Card>

          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <Quote className="w-8 h-8 text-blue-500 mb-3" />
            <p className="text-lg text-gray-800 leading-relaxed mb-4">
              &ldquo;I stopped chasing motivation and started showing up for a
              10-minute practice. It&apos;s unglamorous. It&apos;s also the
              first thing that&apos;s actually changed how I live.&rdquo;
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700">
                M
              </div>
              <div className="text-sm">
                <p className="font-semibold text-gray-900">Maya, 29</p>
                <p className="text-gray-500">Foundation World, 60 days in</p>
              </div>
            </div>
          </Card>

          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <Quote className="w-8 h-8 text-blue-500 mb-3" />
            <p className="text-lg text-gray-800 leading-relaxed mb-4">
              &ldquo;The Community is the part nobody talks about enough. The app
              alone is great; the people in it are why I haven&apos;t quit on a
              bad week.&rdquo;
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700">
                A
              </div>
              <div className="text-sm">
                <p className="font-semibold text-gray-900">Anton, 41</p>
                <p className="text-gray-500">Connection World, 120 days in</p>
              </div>
            </div>
          </Card>
        </div>

        <Card className="bg-white rounded-3xl p-8 shadow-sm mb-12">
          <div className="flex items-start gap-4">
            <Clock className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                A note on honesty
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Levanta is a young system. We don&apos;t have five-year
                transformation stories yet, and we won&apos;t fake them. What we
                have is a growing group of people building slowly, visibly, and
                together — and we would rather show real 60-day progress than
                invent a highlight reel. Come back in a year; the stories will
                be longer.
              </p>
            </div>
          </div>
        </Card>

        <div className="text-center">
          <Link to="/download-app">
            <Button
              data-testid="results-download-cta"
              className="bg-blue-500 hover:bg-blue-600 text-white px-10 py-5 text-lg rounded-xl shadow-md"
            >
              Start your own story
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Results;
